import { VALIDATION } from "../constants";
import { HostSaveDto, WebinarServiceError } from "../types/dto";
import { isValidPhone, normalizePhone } from "../utils/validatePhone";

export type HostFormState = {
  id?: string;
  name: string;
  role: string;
  bio: string;
  creds: string[];
  photo?: string;
  phoneNumber?: string;
};

function normalizeCreds(creds: string[]): string[] {
  return creds
    .map((c) => c.trim())
    .filter(Boolean)
    .slice(0, VALIDATION.credMax)
    .map((c) => c.slice(0, VALIDATION.credCharMax));
}

function sameCreds(a: string[], b: string[]): boolean {
  const left = normalizeCreds(a);
  const right = normalizeCreds(b);
  if (left.length !== right.length) return false;
  return left.every((c, i) => c === right[i]);
}

function normalizedCredsCount(creds: string[]): number {
  return normalizeCreds(creds).length;
}

/** Soft validation for UI enablement / borders. */
export function validateHostForm(
  form: HostFormState,
  opts: { isEdit: boolean },
): {
  nameOk: boolean;
  roleOk: boolean;
  phoneOk: boolean;
  bioOk: boolean;
  photoOk: boolean;
  credsOk: boolean;
  ok: boolean;
} {
  const nameOk = !!form.name.trim();
  const roleOk = !!form.role.trim();
  const bioFilled = !!form.bio.trim();
  const bioOk =
    form.bio.length <= VALIDATION.bioMax &&
    (opts.isEdit ? true : bioFilled);
  const phoneOk = opts.isEdit ? true : isValidPhone(form.phoneNumber || "");
  // Create requires photo + at least one credential; edit keeps existing values optional to change.
  const photoOk = opts.isEdit ? true : !!(form.photo || "").trim();
  const credsOk = opts.isEdit
    ? true
    : normalizedCredsCount(form.creds) >= 1;
  return {
    nameOk,
    roleOk,
    phoneOk,
    bioOk,
    photoOk,
    credsOk,
    ok: nameOk && roleOk && phoneOk && bioOk && photoOk && credsOk,
  };
}

export function hostFormHasChanges(
  form: HostFormState,
  original: HostFormState,
): boolean {
  if (form.name.trim() !== original.name.trim()) return true;
  if (form.role.trim() !== original.role.trim()) return true;
  if (form.bio.trim() !== original.bio.trim()) return true;
  if ((form.photo || "") !== (original.photo || "")) return true;
  if (!sameCreds(form.creds, original.creds)) return true;
  return false;
}

/**
 * Create → full body (phone required, Indian-normalized).
 * Update → only changed fields; phone never included (locked in UI).
 */
export function buildHostPayload(
  form: HostFormState,
  original?: HostFormState | null,
): HostSaveDto {
  const name = form.name.trim();
  const role = form.role.trim();
  const bio = form.bio.trim();
  const creds = normalizeCreds(form.creds);
  const photo = form.photo || "";

  if (!name || !role) {
    throw new WebinarServiceError(
      "VALIDATION",
      "A name and a role are required",
    );
  }
  if (form.bio.length > VALIDATION.bioMax) {
    throw new WebinarServiceError(
      "VALIDATION",
      `Bio must be ≤ ${VALIDATION.bioMax} characters`,
    );
  }

  // Create — every profile field is required
  if (!form.id) {
    const phoneNumber = normalizePhone(form.phoneNumber || "");
    if (!phoneNumber) {
      throw new WebinarServiceError(
        "VALIDATION",
        "Enter a valid Indian mobile number (10 digits, starting 6–9)",
      );
    }
    if (!bio) {
      throw new WebinarServiceError("VALIDATION", "Bio is required");
    }
    if (!photo.trim()) {
      throw new WebinarServiceError(
        "VALIDATION",
        "Profile picture is required",
      );
    }
    if (creds.length < 1) {
      throw new WebinarServiceError(
        "VALIDATION",
        "Add at least one credential",
      );
    }
    return {
      name,
      role,
      bio,
      creds,
      photo,
      phoneNumber,
    };
  }

  // Update — diff against original (or empty baseline)
  const baseline: HostFormState = original || {
    id: form.id,
    name: "",
    role: "",
    bio: "",
    creds: [],
    photo: "",
    phoneNumber: form.phoneNumber,
  };

  const patch: HostSaveDto = { id: form.id };
  if (name !== baseline.name.trim()) patch.name = name;
  if (role !== baseline.role.trim()) patch.role = role;
  if (bio !== baseline.bio.trim()) patch.bio = bio;
  if (!sameCreds(form.creds, baseline.creds)) patch.creds = creds;
  if (photo !== (baseline.photo || "")) {
    patch.photo = photo;
  }

  const keys = Object.keys(patch).filter((k) => k !== "id");
  if (keys.length === 0) {
    throw new WebinarServiceError("VALIDATION", "No changes to save");
  }

  return patch;
}

export function validateHostPhoto(file: File): string | null {
  if (!VALIDATION.photoTypes.includes(file.type)) {
    return "Use a JPG or PNG image";
  }
  if (file.size > VALIDATION.photoMaxBytes) {
    return "Image must be 512KB or smaller";
  }
  return null;
}
